import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Menu,
    MenuItem,
    Checkbox,
    Tooltip,
    IconButton,
    Badge,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import Swal from 'sweetalert2';
import { alpha, type Theme } from '@mui/material/styles';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIButton } from '../../atomic/atoms';
import {
    GetS3List,
    S3FileItem,
    S3FolderItem,
    S3ListResponse,
    FetchS3FileBlob,
    MAX_S3_SELECTION_KEYS,
    saveBlobAsFile,
    getFileNameFromKey,
    openS3ImageInNewTab,
} from '../../services/query/aiPrompt/S3BrowserQuery.services';
import { CreateS3Folder, RenameS3Object } from '../../services/command/aiPrompt/S3BrowserCommand.services';

const formatBytes = (n: number) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const isValidFolderSegment = (name: string) => {
    const s = name.trim();
    if (!s || s.length > 255) return false;
    if (s === '.' || s === '..') return false;
    if (s.includes('/') || s.includes('\\') || /[\0\r\n]/.test(s)) return false;
    return true;
};

const isPreviewableImageKey = (key: string) => /\.(jpe?g|png|gif|webp)$/i.test(key);

const canOpenFile = (file: S3FileItem) => Boolean(file.url) || isPreviewableImageKey(file.key);

type CtxTarget = { kind: 'folder'; item: S3FolderItem } | { kind: 'file'; item: S3FileItem };

type CtxMenuState = { mouseX: number; mouseY: number; target: CtxTarget };

const toolbarIconButtonSx = (theme: Theme) => ({
    width: 52,
    height: 52,
    borderRadius: 2,
    transition: 'background-color 0.2s ease, transform 0.15s ease',
    '&:hover:not(:disabled)': {
        bgcolor: alpha(theme.palette.primary.main, 0.12),
        transform: 'translateY(-1px)',
    },
    '& .MuiSvgIcon-root': { fontSize: 28 },
});

const newFolderIconButtonSx = (theme: Theme) => ({
    ...toolbarIconButtonSx(theme),
    bgcolor: alpha(theme.palette.primary.main, 0.14),
    color: theme.palette.primary.main,
    '&:hover:not(:disabled)': {
        bgcolor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        transform: 'translateY(-1px)',
    },
});

const AIPromptS3BrowserTest: React.FC = () => {
    const { t } = useTranslation();
    const basePrefix = React.useMemo(() => {
        // sonar-disable-next-line S5852
        const env = String(import.meta.env.VITE_ENVIRONMENT || 'DEV')
            .trim()
            .replace(/\/+$/, '');
        return env ? `${env}/` : '';
    }, []);
    const [prefix, setPrefix] = useState(basePrefix);
    const [folders, setFolders] = useState<S3FolderItem[]>([]);
    const [files, setFiles] = useState<S3FileItem[]>([]);
    const [isTruncated, setIsTruncated] = useState(false);
    const [nextToken, setNextToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [folderDialogOpen, setFolderDialogOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [creatingFolder, setCreatingFolder] = useState(false);
    const [ctxMenu, setCtxMenu] = useState<CtxMenuState | null>(null);
    const [renameOpen, setRenameOpen] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [renameTarget, setRenameTarget] = useState<CtxTarget | null>(null);
    const [renaming, setRenaming] = useState(false);
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set());

    const applyListResponse = useCallback((data: S3ListResponse, append: boolean) => {
        setFolders((prev) => {
            const next = append ? [...prev, ...(data.folders || [])] : [...(data.folders || [])];
            const seen = new Set<string>();
            return next.filter((f) => {
                if (seen.has(f.key)) return false;
                seen.add(f.key);
                return true;
            });
        });
        setFiles((prev) => {
            const next = append ? [...prev, ...(data.files || [])] : [...(data.files || [])];
            const seen = new Set<string>();
            return next.filter((f) => {
                if (seen.has(f.key)) return false;
                seen.add(f.key);
                return true;
            });
        });
        setIsTruncated(!!data.isTruncated);
        setNextToken(data.nextContinuationToken || null);
        setPrefix(data.prefix ?? '');
    }, []);

    const load = useCallback(
        async (targetPrefix: string, continuationToken?: string | null, append = false) => {
            if (!append) {
                setSelectedKeys(new Set());
            }
            try {
                setLoading(true);
                setError(null);
                const data = await GetS3List({
                    prefix: targetPrefix,
                    continuationToken: continuationToken || undefined,
                });
                applyListResponse(data, append);
            } catch {
                setError(t('aiPrompt.s3Browser.loadError'));
                if (!append) {
                    setFolders([]);
                    setFiles([]);
                    setIsTruncated(false);
                    setNextToken(null);
                }
            } finally {
                setLoading(false);
            }
        },
        [applyListResponse, t]
    );

    useEffect(() => {
        load(basePrefix, null, false);
    }, [load, basePrefix]);

    const goUp = () => {
        const p = prefix.replace(/\/$/, '');
        if (!p || p === basePrefix.replace(/\/$/, '')) return;
        const idx = p.lastIndexOf('/');
        const parent = idx <= 0 ? '' : `${p.slice(0, idx + 1)}`;
        load(parent, null, false);
    };

    const openFolder = (key: string) => {
        load(key, null, false);
    };

    const openFile = (file: S3FileItem) => {
        if (isPreviewableImageKey(file.key)) {
            void (async () => {
                try {
                    await openS3ImageInNewTab(file.key);
                } catch {
                    await Swal.fire({ icon: 'error', title: t('aiPrompt.s3Browser.downloadError') });
                }
            })();
            return;
        }
        if (file.url) {
            window.open(file.url, '_blank', 'noopener,noreferrer');
        }
    };

    const loadMore = () => {
        if (nextToken) {
            load(prefix, nextToken, true);
        }
    };

    const openFolderDialog = () => {
        setNewFolderName('');
        setFolderDialogOpen(true);
    };

    const closeFolderDialog = () => {
        if (!creatingFolder) {
            setFolderDialogOpen(false);
            setNewFolderName('');
        }
    };

    const submitNewFolder = async () => {
        if (!isValidFolderSegment(newFolderName)) {
            await Swal.fire({
                icon: 'warning',
                title: t('aiPrompt.s3Browser.invalidFolderName'),
            });
            return;
        }

        try {
            setCreatingFolder(true);
            const trimmedName = newFolderName.trim();
            await CreateS3Folder({
                prefix,
                name: trimmedName,
            });
            setFolderDialogOpen(false);
            setNewFolderName('');
            const newPrefix = prefix.endsWith('/') ? `${prefix}${trimmedName}/` : `${prefix}/${trimmedName}/`;
            await load(newPrefix, null, false);
            await Swal.fire({
                icon: 'success',
                title: t('aiPrompt.s3Browser.createFolderSuccess'),
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (err: unknown) {
            const data = (err as { data?: { message?: string } })?.data;
            const msg = data?.message ?? t('aiPrompt.s3Browser.createFolderError');
            await Swal.fire({ icon: 'error', title: msg });
        } finally {
            setCreatingFolder(false);
        }
    };

    const closeCtxMenu = () => setCtxMenu(null);

    const openContextMenu = (e: React.MouseEvent, target: CtxTarget) => {
        e.preventDefault();
        e.stopPropagation();
        setCtxMenu({ mouseX: e.clientX, mouseY: e.clientY, target });
    };

    const getRenameSegment = (target: CtxTarget) => getFileNameFromKey(target.item.key);

    const openRenameDialog = (target: CtxTarget) => {
        setRenameTarget(target);
        setRenameValue(getRenameSegment(target));
        setRenameOpen(true);
        closeCtxMenu();
    };

    const closeRenameDialog = () => {
        if (!renaming) {
            setRenameOpen(false);
            setRenameTarget(null);
            setRenameValue('');
        }
    };

    const submitRename = async () => {
        if (!renameTarget) return;
        const segment = renameValue.trim();
        if (renameTarget.kind === 'folder') {
            if (!isValidFolderSegment(segment)) {
                await Swal.fire({ icon: 'warning', title: t('aiPrompt.s3Browser.invalidFolderName') });
                return;
            }
        } else if (
            !segment ||
            segment.length > 255 ||
            segment.includes('/') ||
            segment.includes('\\') ||
            /[\0\r\n]/.test(segment)
        ) {
            await Swal.fire({ icon: 'warning', title: t('aiPrompt.s3Browser.invalidFolderName') });
            return;
        }

        const sourceKey = renameTarget.item.key;

        try {
            setRenaming(true);
            await RenameS3Object({ sourceKey, newName: segment });
            setRenameOpen(false);
            setRenameTarget(null);
            setRenameValue('');
            await load(prefix, null, false);
            await Swal.fire({
                icon: 'success',
                title: t('aiPrompt.s3Browser.renameSuccess'),
                timer: 1800,
                showConfirmButton: false,
            });
        } catch (err: unknown) {
            const data = (err as { data?: { message?: string } })?.data;
            const msg = data?.message ?? t('aiPrompt.s3Browser.renameError');
            await Swal.fire({ icon: 'error', title: msg });
        } finally {
            setRenaming(false);
        }
    };

    const downloadFile = async (file: S3FileItem) => {
        closeCtxMenu();
        try {
            setLoading(true);
            const blob = await FetchS3FileBlob(file.key, false);
            saveBlobAsFile(blob, getFileNameFromKey(file.key));
        } catch {
            await Swal.fire({ icon: 'error', title: t('aiPrompt.s3Browser.downloadError') });
        } finally {
            setLoading(false);
        }
    };

    const downloadFolder = async (folderKey: string) => {
        closeCtxMenu();
        try {
            setLoading(true);
            const response = await GetS3List({ prefix: folderKey });
            if (!response.files || response.files.length === 0) {
                await Swal.fire({ icon: 'info', title: t('aiPrompt.s3Browser.empty') });
                return;
            }
            for (const file of response.files) {
                const blob = await FetchS3FileBlob(file.key, false);
                saveBlobAsFile(blob, getFileNameFromKey(file.key));
            }
        } catch {
            await Swal.fire({ icon: 'error', title: t('aiPrompt.s3Browser.downloadFolderError') });
        } finally {
            setLoading(false);
        }
    };

    const toggleFileSelected = (fileKey: string) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(fileKey)) {
                next.delete(fileKey);
                return next;
            }
            if (next.size >= MAX_S3_SELECTION_KEYS) {
                void Swal.fire({
                    icon: 'warning',
                    title: t('aiPrompt.s3Browser.selectionLimitReached', { max: MAX_S3_SELECTION_KEYS }),
                });
                return prev;
            }
            next.add(fileKey);
            return next;
        });
    };

    const selectAllFilesOnPage = () => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            for (const f of files) {
                if (next.size >= MAX_S3_SELECTION_KEYS) {
                    void Swal.fire({
                        icon: 'warning',
                        title: t('aiPrompt.s3Browser.selectionLimitReached', { max: MAX_S3_SELECTION_KEYS }),
                    });
                    break;
                }
                next.add(f.key);
            }
            return next;
        });
    };

    const clearFileSelection = () => setSelectedKeys(new Set());

    const downloadSelected = async () => {
        if (selectedKeys.size === 0) {
            await Swal.fire({ icon: 'info', title: t('aiPrompt.s3Browser.nothingSelected') });
            return;
        }
        try {
            setLoading(true);
            for (const key of selectedKeys) {
                const blob = await FetchS3FileBlob(key, false);
                saveBlobAsFile(blob, getFileNameFromKey(key));
            }
        } catch {
            await Swal.fire({ icon: 'error', title: t('aiPrompt.s3Browser.downloadError') });
        } finally {
            setLoading(false);
        }
    };

    const displayPath = prefix || t('aiPrompt.s3Browser.root');

    return (
        <SCUIContent headerName={t('aiPrompt.s3Browser.title')}>
            <Box
                sx={(theme) => ({
                    mb: 2,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    alignItems: 'center',
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                    boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
                })}
            >
                <Tooltip title={t('aiPrompt.s3Browser.parentFolder')} enterDelay={400}>
                    <span>
                        <IconButton
                            onClick={goUp}
                            disabled={loading || !prefix || prefix === basePrefix}
                            color="default"
                            aria-label={t('aiPrompt.s3Browser.parentFolder')}
                            sx={(theme) => toolbarIconButtonSx(theme)}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t('aiPrompt.s3Browser.newFolder')} enterDelay={400}>
                    <span>
                        <IconButton
                            onClick={openFolderDialog}
                            disabled={loading}
                            aria-label={t('aiPrompt.s3Browser.newFolder')}
                            sx={(theme) => newFolderIconButtonSx(theme)}
                        >
                            <CreateNewFolderIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t('aiPrompt.s3Browser.selectAllOnPage')} enterDelay={400}>
                    <span>
                        <IconButton
                            onClick={selectAllFilesOnPage}
                            disabled={loading || files.length === 0}
                            color="default"
                            aria-label={t('aiPrompt.s3Browser.selectAllOnPage')}
                            sx={(theme) => toolbarIconButtonSx(theme)}
                        >
                            <SelectAllIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t('aiPrompt.s3Browser.clearSelection')} enterDelay={400}>
                    <span>
                        <IconButton
                            onClick={clearFileSelection}
                            disabled={loading || selectedKeys.size === 0}
                            color="default"
                            aria-label={t('aiPrompt.s3Browser.clearSelection')}
                            sx={(theme) => toolbarIconButtonSx(theme)}
                        >
                            <ClearAllIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t('aiPrompt.s3Browser.downloadSelected')} enterDelay={400}>
                    <span>
                        <IconButton
                            onClick={() => void downloadSelected()}
                            disabled={loading || selectedKeys.size === 0}
                            color="default"
                            aria-label={t('aiPrompt.s3Browser.downloadSelected')}
                            sx={(theme) => toolbarIconButtonSx(theme)}
                        >
                            <FileDownloadIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Box
                    sx={(theme) => ({
                        ml: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        pl: 1,
                        borderLeft: `1px solid ${theme.palette.divider}`,
                    })}
                >
                    <Tooltip
                        title={
                            <>
                                {t('aiPrompt.s3Browser.path')}: {displayPath}
                            </>
                        }
                        enterDelay={400}
                    >
                        <FolderSpecialIcon
                            sx={{
                                fontSize: 30,
                                cursor: 'help',
                                color: 'primary.main',
                                opacity: 0.85,
                                filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.08))',
                            }}
                            aria-hidden
                        />
                    </Tooltip>
                    <Tooltip
                        title={t('aiPrompt.s3Browser.selectedCount', {
                            count: selectedKeys.size,
                            max: MAX_S3_SELECTION_KEYS,
                        })}
                        enterDelay={400}
                    >
                        <Badge
                            badgeContent={selectedKeys.size}
                            color="secondary"
                            invisible={selectedKeys.size === 0}
                            max={MAX_S3_SELECTION_KEYS}
                            sx={{
                                '& .MuiBadge-badge': {
                                    fontSize: '0.7rem',
                                    minWidth: 20,
                                    height: 20,
                                    right: 6,
                                    top: 6,
                                    fontWeight: 700,
                                },
                            }}
                        >
                            <LibraryAddCheckIcon
                                sx={{
                                    fontSize: 30,
                                    cursor: 'help',
                                    color: 'secondary.main',
                                    opacity: 0.9,
                                    display: 'block',
                                    filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.08))',
                                }}
                                aria-hidden
                            />
                        </Badge>
                    </Tooltip>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {isTruncated && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('aiPrompt.s3Browser.truncatedWarning')}
                </Alert>
            )}

            <Paper variant="outlined" onContextMenu={(e) => e.preventDefault()}>
                <List dense disablePadding>
                    {folders.map((f) => (
                        <ListItemButton
                            key={f.key}
                            onClick={() => openFolder(f.key)}
                            onContextMenu={(e) => openContextMenu(e, { kind: 'folder', item: f })}
                            disabled={loading}
                        >
                            <ListItemIcon>
                                <FolderIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={f.name} secondary={f.key} />
                        </ListItemButton>
                    ))}
                    {files.map((file) => (
                        <ListItem
                            key={file.key}
                            disablePadding
                            secondaryAction={
                                <Checkbox
                                    edge="end"
                                    tabIndex={-1}
                                    checked={selectedKeys.has(file.key)}
                                    onChange={() => toggleFileSelected(file.key)}
                                    onClick={(e) => e.stopPropagation()}
                                    disabled={loading}
                                    slotProps={{
                                        input: {
                                            'aria-label': t('aiPrompt.s3Browser.selectFileForDownloadAria'),
                                        },
                                    }}
                                />
                            }
                        >
                            <ListItemButton
                                onClick={() => openFile(file)}
                                onContextMenu={(e) => openContextMenu(e, { kind: 'file', item: file })}
                                disabled={loading && !canOpenFile(file)}
                                dense
                            >
                                <ListItemIcon>
                                    <InsertDriveFileIcon color="action" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={file.name}
                                    secondary={`${file.key} · ${formatBytes(file.size)}${
                                        file.lastModified ? ` · ${file.lastModified}` : ''
                                    }`}
                                />
                                {canOpenFile(file) ? (
                                    <OpenInNewIcon fontSize="small" color="action" sx={{ ml: 1 }} />
                                ) : null}
                            </ListItemButton>
                        </ListItem>
                    ))}
                    {!loading && folders.length === 0 && files.length === 0 && !error && (
                        <Box sx={{ p: 3 }}>
                            <Typography color="text.secondary">{t('aiPrompt.s3Browser.empty')}</Typography>
                        </Box>
                    )}
                </List>
            </Paper>

            {nextToken && (
                <Box sx={{ mt: 2 }}>
                    <SCUIButton
                        type="secondary"
                        sizeType="default"
                        size="m"
                        text={t('aiPrompt.s3Browser.loadMore')}
                        onClick={loadMore}
                        disabled={loading}
                    />
                </Box>
            )}

            {loading && (
                <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
                    {t('aiPrompt.s3Browser.loading')}
                </Typography>
            )}

            <Menu
                open={ctxMenu !== null}
                onClose={closeCtxMenu}
                anchorReference="anchorPosition"
                anchorPosition={ctxMenu !== null ? { top: ctxMenu.mouseY, left: ctxMenu.mouseX } : undefined}
                slotProps={{
                    paper: {
                        onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
                    },
                }}
            >
                {ctxMenu === null
                    ? null
                    : ctxMenu.target.kind === 'folder'
                      ? [
                            <MenuItem
                                key="open"
                                onClick={() => {
                                    openFolder(ctxMenu.target.item.key);
                                    closeCtxMenu();
                                }}
                            >
                                <ListItemIcon>
                                    <FolderOpenIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('aiPrompt.s3Browser.ctxOpen')} />
                            </MenuItem>,
                            <MenuItem key="rename" onClick={() => openRenameDialog(ctxMenu.target)}>
                                <ListItemIcon>
                                    <DriveFileRenameOutlineIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('aiPrompt.s3Browser.ctxRename')} />
                            </MenuItem>,
                            <MenuItem
                                key="download-folder"
                                onClick={() => void downloadFolder(ctxMenu.target.item.key)}
                            >
                                <ListItemIcon>
                                    <FileDownloadIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={t('aiPrompt.s3Browser.ctxDownloadFolder')} />
                            </MenuItem>,
                        ]
                      : (() => {
                            const target = ctxMenu.target;
                            if (target.kind !== 'file') return null;
                            const file = target.item;
                            return [
                                <MenuItem
                                    key="open"
                                    onClick={() => {
                                        openFile(file);
                                        closeCtxMenu();
                                    }}
                                    disabled={!canOpenFile(file)}
                                >
                                    <ListItemIcon>
                                        <OpenInNewIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={t('aiPrompt.s3Browser.ctxOpen')} />
                                </MenuItem>,
                                <MenuItem key="rename" onClick={() => openRenameDialog(target)}>
                                    <ListItemIcon>
                                        <DriveFileRenameOutlineIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={t('aiPrompt.s3Browser.ctxRename')} />
                                </MenuItem>,
                                <MenuItem key="dl" onClick={() => void downloadFile(file)}>
                                    <ListItemIcon>
                                        <FileDownloadIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={t('aiPrompt.s3Browser.ctxDownload')} />
                                </MenuItem>,
                            ];
                        })()}
            </Menu>

            <Dialog open={folderDialogOpen} onClose={closeFolderDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{t('aiPrompt.s3Browser.newFolderTitle')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('aiPrompt.s3Browser.folderName')}
                        placeholder={t('aiPrompt.s3Browser.folderNamePlaceholder')}
                        fullWidth
                        variant="outlined"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        disabled={creatingFolder}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                void submitNewFolder();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <SCUIButton
                        type="ghost"
                        sizeType="default"
                        size="m"
                        text={t('aiPrompt.s3Browser.cancel')}
                        onClick={closeFolderDialog}
                        disabled={creatingFolder}
                    />
                    <SCUIButton
                        type="primary"
                        sizeType="default"
                        size="m"
                        text={t('aiPrompt.s3Browser.createFolder')}
                        onClick={() => void submitNewFolder()}
                        disabled={creatingFolder}
                    />
                </DialogActions>
            </Dialog>

            <Dialog open={renameOpen} onClose={closeRenameDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{t('aiPrompt.s3Browser.renameTitle')}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label={t('aiPrompt.s3Browser.newName')}
                        fullWidth
                        variant="outlined"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        disabled={renaming}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                void submitRename();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <SCUIButton
                        type="ghost"
                        sizeType="default"
                        size="m"
                        text={t('aiPrompt.s3Browser.cancel')}
                        onClick={closeRenameDialog}
                        disabled={renaming}
                    />
                    <SCUIButton
                        type="primary"
                        sizeType="default"
                        size="m"
                        text={t('aiPrompt.s3Browser.renameSave')}
                        onClick={() => void submitRename()}
                        disabled={renaming}
                    />
                </DialogActions>
            </Dialog>
        </SCUIContent>
    );
};

export default AIPromptS3BrowserTest;
